import { useCallback, useEffect, useState } from "react";
import { IVehicle } from "../../models/vehicle";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { AppState } from "../../store/configureStore";
import { getVehicles } from "../../services/vehiclesService";
import { VehicleRow } from "./VehicleRow";
import { Icon, Loader, Pagination, Segment, Table, Button } from "semantic-ui-react";
import { AddVehicleForm } from "./AddVehicleForm";
import { UserRole } from "../../actions/authentificationActions";
import "./Vehicles.css";

export const Vehicles = () => {
    const user = useSelector((state: AppState) => state.user);
    const [loading, setLoading] = useState(true);
    const loc = useLocation();
    const [page, setPage] = useState(loc.state ? (loc.state as number) : 1);
    const [totalRows, setTotalRows] = useState(0);
    const [vehicles, setVehicles] = useState<IVehicle[]>([]);
    const [modalOpen, setModalOpen] = useState(false);

    const fetchVehicles = useCallback(async () => {
        setLoading(true);
        const res = await getVehicles(page);
        if (res.rows) {
            setVehicles(res.rows as IVehicle[]);
            setTotalRows(res.totalRows);
        }
        setLoading(false);
    }, [page]);

    useEffect(() => {
        fetchVehicles();
    }, [fetchVehicles]);

    const handleRemoveVehicle = (d: IVehicle) => setVehicles([...vehicles].filter(x => x !== d));

    const handleFetchVehicles = () => {
        setPage(1);
        fetchVehicles();
    };

    return (
        <div className="vehicles-container">
            <div className="container-header">
                <h1>Vehicles</h1>
                {user.role === UserRole.Admin && (
                    <Button
                        secondary
                        content="Add new vehicle"
                        icon="plus"
                        onClick={() => setModalOpen(true)}
                    />
                )}
            </div>
            <div className="vehicles-display">
                <div className="vehicles-table">
                    {loading ? (
                        <Loader active inline="centered" size="huge" />
                    ) : (
                        <Segment.Group>
                            <Segment />
                            <Segment className="table-container">
                                <Table basic="very">
                                    <Table.Header>
                                        <Table.Row>
                                            <Table.HeaderCell>Manufacturer</Table.HeaderCell>
                                            <Table.HeaderCell>Model</Table.HeaderCell>
                                            <Table.HeaderCell>VIN</Table.HeaderCell>
                                            <Table.HeaderCell>Registration Plate</Table.HeaderCell>
                                            <Table.HeaderCell>Color</Table.HeaderCell>
                                            <Table.HeaderCell>Active</Table.HeaderCell>
                                            <Table.HeaderCell>Actions</Table.HeaderCell>
                                        </Table.Row>
                                    </Table.Header>
                                    <Table.Body>
                                        {vehicles.length === 0 ? (
                                            <Table.Row>
                                                <Table.Cell textAlign="center" colSpan="5">
                                                    No matching records found
                                                </Table.Cell>
                                            </Table.Row>
                                        ) : (
                                            vehicles.map(x => (
                                                <VehicleRow
                                                    key={x.guid}
                                                    vehicle={x}
                                                    handleRemoveVehicle={handleRemoveVehicle}
                                                    page={page + ""}
                                                />
                                            ))
                                        )}
                                        <Table.Row />
                                    </Table.Body>
                                </Table>
                            </Segment>
                            <Segment>
                                {totalRows > 10 && (
                                    <Pagination
                                        size="small"
                                        activePage={page}
                                        onPageChange={(e, data: any) => setPage(+data.activePage)}
                                        ellipsisItem={{
                                            content: <Icon name="ellipsis horizontal" />,
                                        }}
                                        firstItem={{
                                            content: <Icon name="angle double left" />,
                                        }}
                                        lastItem={{
                                            content: <Icon name="angle double right" />,
                                        }}
                                        prevItem={{ content: <Icon name="angle left" /> }}
                                        nextItem={{ content: <Icon name="angle right" /> }}
                                        totalPages={Math.ceil(totalRows / 10)}
                                    />
                                )}
                            </Segment>
                            <AddVehicleForm
                                open={modalOpen}
                                closeModal={() => setModalOpen(false)}
                                handleAddVehicle={handleFetchVehicles}
                            />
                        </Segment.Group>
                    )}
                </div>
            </div>
        </div>
    );
};
